import FoodsApi from "@/packages/api/Api/V1/FoodsApi";
import { jsonHeaders } from "@/packages/dashboard/csrf";
import { appendQueryParams, withAppDomain } from "@/packages/dashboard/utils/url-utils";
import { foodItemSchema } from "@/packages/dashboard/api/get-meals";

import { Box, CloseButton, Combobox, Loader, Stack, Text, TextInput, useCombobox } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import type { Ref } from "react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import type { FoodItem as ApiFoodItem } from "@/packages/dashboard/api/get-meals";

export type FoodItem = ApiFoodItem;

type FoodItemComboboxProps = {
  value: FoodItem | null;
  onChange: (item: FoodItem | null) => void;
  onRequestCreate?: (name: string) => void;
  label?: string;
  placeholder?: string;
  limit?: number;
  inputRef?: Ref<HTMLInputElement>;
};

const DEFAULT_LIMIT = 20;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 150;
const foodAutocompleteResponseSchema = z.array(foodItemSchema);

const formatServingQuantityValue = (value: number | string) => {
  const numericValue = typeof value === "number" ? value : Number(value);
  if (!numericValue) {
    return String(value);
  }

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export const formatServingSummary = (item: FoodItem) => {
  const quantity = formatServingQuantityValue(item.serving_size);
  const unit = item.serving_unit.trim();
  return `${quantity} ${unit}`.trim();
};

export const FoodItemCombobox = ({
  value,
  onChange,
  onRequestCreate,
  label,
  placeholder = "Search food",
  limit = DEFAULT_LIMIT,
  inputRef,
}: FoodItemComboboxProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [searchValue, setSearchValue] = useState(() => value?.name ?? "");
  const [debouncedSearch] = useDebouncedValue(searchValue.trim(), SEARCH_DEBOUNCE_MS);
  const isBelowMinimumSearch = debouncedSearch.length < MIN_SEARCH_LENGTH;

  useEffect(() => {
    setSearchValue(value?.name ?? "");
  }, [value?.id, value?.name]);

  const {
    data: responseItems = [],
    isFetching,
    error,
  } = useQuery<FoodItem[]>({
    queryKey: ["foods-autocomplete", debouncedSearch, limit],
    queryFn: async ({ signal }) => {
      const url = appendQueryParams(withAppDomain(FoodsApi.autocomplete.path()), { q: debouncedSearch, limit });
      const response = await fetch(url, { headers: jsonHeaders(), signal });

      if (!response.ok) {
        throw new Error(`Food autocomplete request failed with status ${response.status}`);
      }

      const data = await response.json();
      return foodAutocompleteResponseSchema.parse(data);
    },
    enabled: !isBelowMinimumSearch,
    staleTime: 60_000,
    gcTime: 60_000,
  });

  const itemsById = useMemo(() => {
    const map = new Map<number, FoodItem>();
    responseItems?.forEach((item) => map.set(item.id, item));
    return map;
  }, [responseItems]);

  const trimmedSearchValue = searchValue.trim();
  const showCreateOption = trimmedSearchValue.length > 0;
  const createOptionValue = "__create_new_food_item__";

  const handleOptionSubmit = (optionValue: string) => {
    if (optionValue === createOptionValue) {
      if (trimmedSearchValue.length > 0) {
        onRequestCreate?.(trimmedSearchValue);
      }
      combobox.closeDropdown();
      return;
    }

    const selectedItem = itemsById.get(Number(optionValue));

    if (selectedItem) {
      onChange(selectedItem);
      setSearchValue(selectedItem.name);
    }

    combobox.closeDropdown();
  };

  const clearSelection = () => {
    setSearchValue("");
    onChange(null);
    combobox.openDropdown();
    combobox.resetSelectedOption();
  };

  useEffect(() => {
    if (!combobox.dropdownOpened) {
      return;
    }

    if (isBelowMinimumSearch) {
      combobox.resetSelectedOption();
      return;
    }

    // Always select the first option - even if nothing is found, we'll have the "Create X" option, and
    // we want that also to be selected so we can fluidly create new items
    combobox.selectFirstOption();
  }, [combobox, combobox.dropdownOpened, isBelowMinimumSearch, responseItems]);

  const submitSelectedOption = () => {
    if (isFetching || isBelowMinimumSearch) {
      return;
    }

    if (combobox.getSelectedOptionIndex() === -1) {
      combobox.selectFirstOption();
    }

    if (combobox.getSelectedOptionIndex() === -1) {
      return;
    }

    combobox.clickSelectedOption();
  };

  const nothingFound = !isBelowMinimumSearch && !isFetching && responseItems.length === 0;

  const showError = Boolean(error);

  return (
    <Combobox store={combobox} withinPortal={false} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <TextInput
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          value={searchValue}
          onChange={(event) => {
            const nextValue = event.currentTarget.value;
            setSearchValue(nextValue);

            if (!combobox.dropdownOpened) {
              combobox.openDropdown();
            }
          }}
          onFocus={() => combobox.openDropdown()}
          onClick={() => combobox.openDropdown()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submitSelectedOption();
              return;
            }

            if (event.key === "Delete" && value && searchValue === value.name) {
              const input = event.currentTarget;
              const { selectionStart, selectionEnd, value: inputValue } = input;
              if (selectionStart === 0 && selectionEnd === inputValue.length) {
                event.preventDefault();
                clearSelection();
              }
            }
          }}
          rightSection={
            <Box>
              {isFetching ? (
                <Loader size="xs" />
              ) : value ? (
                <CloseButton
                  aria-label="Clear selection"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={clearSelection}
                  tabIndex={-1}
                />
              ) : null}
            </Box>
          }
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {showError ? (
            <Combobox.Empty>Unable to load options</Combobox.Empty>
          ) : (
            <>
              {isBelowMinimumSearch ? (
                <Combobox.Empty>Type at least {MIN_SEARCH_LENGTH} characters to search</Combobox.Empty>
              ) : (
                <>
                  {responseItems.map((item) => {
                    const servingSummary = formatServingSummary(item);
                    const servingDescription = item.serving_description?.trim();
                    const itemDescription = item.description?.trim();

                    return (
                      <Combobox.Option value={String(item.id)} key={item.id}>
                        <Stack gap={2}>
                          <Text fw={500}>{item.name}</Text>
                          <Text size="xs">Serving: {servingSummary}</Text>
                          {servingDescription && <Text size="xs">{servingDescription}</Text>}
                          {itemDescription && <Text size="xs">{itemDescription}</Text>}
                        </Stack>
                      </Combobox.Option>
                    );
                  })}

                  {showCreateOption && (
                    <Combobox.Option value={createOptionValue} key={createOptionValue}>
                      <Text fw={500}>
                        Create &ldquo;
                        {trimmedSearchValue}
                        &rdquo;
                      </Text>
                    </Combobox.Option>
                  )}

                  {nothingFound && <Combobox.Empty>Nothing found</Combobox.Empty>}
                </>
              )}
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
